import { useState, useEffect } from 'react'
import { useAppDispatch, useAppSelector } from '@state/hooks'
import { setActiveCompany, updateCompany, deleteCompanyAsync } from '@state/slices/companiesSlice'
import { setOnboardingVisible } from '@state/slices/onboardingSlice'
import { NEW_COMPANY_CTA_ENABLED } from '../../config/featureGates'
import { showCurrentlyDisabledToast } from '@utils/toastApi'
import './CompanySelector.css'

const API_BASE = import.meta.env.VITE_API_BASE_URL || 'http://127.0.0.1:8000'

function CompanySelector() {
  const dispatch = useAppDispatch()
  const { companies, activeCompanyId } = useAppSelector(state => state.companies)
  const activeCompany = companies.find(c => c.id === activeCompanyId)
  const ivpIndustryLabel = useAppSelector(state => state.ivp?.ivp?.industry_label || '')
  const userId = useAppSelector(state => state.auth?.user?.id) || 1

  const [showDropdown, setShowDropdown] = useState(false)
  const [healthScores, setHealthScores] = useState<Record<string, number>>({})
  const [showEditModal, setShowEditModal] = useState(false)
  const [formData, setFormData] = useState({ name: '', industry: '', description: '' })
  const [companyToDelete, setCompanyToDelete] = useState<{ id: string; name: string; isSample?: boolean } | null>(null)
  const [deleting, setDeleting] = useState(false)
  const [deleteError, setDeleteError] = useState<string | null>(null)

  useEffect(() => {
    if (!showDropdown || companies.length === 0) return
    const token = localStorage.getItem('jwt_token')
    fetch(`${API_BASE}/portfolio/${userId}/companies`, {
      headers: token ? { 'Authorization': `Bearer ${token}` } : {},
    })
      .then(r => r.ok ? r.json() : null)
      .then(data => {
        if (data?.companies) {
          const scores: Record<string, number> = {}
          data.companies.forEach((c: any) => {
            scores[`company_${c.id}`] = c.health_score
          })
          setHealthScores(scores)
        }
      })
      .catch(() => {})
  }, [showDropdown, companies, userId])

  const handleSelectCompany = (companyId: string) => {
    dispatch(setActiveCompany(companyId))
    setShowDropdown(false)
  }

  const handleEditCompany = (e: React.FormEvent) => {
    e.preventDefault()
    if (activeCompanyId && formData.name.trim()) {
      dispatch(updateCompany({
        id: activeCompanyId,
        name: formData.name.trim(),
        industry: formData.industry.trim() || undefined,
        description: formData.description.trim() || undefined,
      }))
      setFormData({ name: '', industry: '', description: '' })
      setShowEditModal(false)
    }
  }

  const handleDeleteCompany = (companyId: string, e: React.MouseEvent) => {
    e.stopPropagation()
    const company = companies.find(c => c.id === companyId)
    if (!company) return
    setDeleteError(null)
    setCompanyToDelete({ id: company.id, name: company.name, isSample: company.isSample })
  }

  const confirmDelete = async () => {
    if (!companyToDelete) return
    setDeleting(true)
    setDeleteError(null)
    try {
      const result = await dispatch(deleteCompanyAsync(companyToDelete.id))
      if (deleteCompanyAsync.rejected.match(result)) {
        setDeleteError((result.payload as string) || 'Failed to delete company')
        setDeleting(false)
        return
      }
      setCompanyToDelete(null)
      setDeleting(false)
      setShowDropdown(false)
    } catch (err: any) {
      setDeleteError(err?.message || 'Failed to delete company')
      setDeleting(false)
    }
  }

  const cancelDelete = () => {
    if (deleting) return
    setCompanyToDelete(null)
    setDeleteError(null)
  }

  const openOnboardingWizard = () => {
    if (!NEW_COMPANY_CTA_ENABLED) {
      showCurrentlyDisabledToast()
      return
    }
    dispatch(setOnboardingVisible(true))
    setShowDropdown(false)
  }

  const openEditModal = () => {
    if (activeCompany) {
      setFormData({
        name: activeCompany.name,
        industry: activeCompany.industry || '',
        description: activeCompany.description || '',
      })
      setShowEditModal(true)
      setShowDropdown(false)
    }
  }

  return (
    <div className="company-selector">
      <div className="company-selector-header">
        <button
          className="company-display"
          onClick={() => setShowDropdown(!showDropdown)}
        >
          <div className="company-icon">🏢</div>
          <div className="company-info">
            <div className="company-name">
              {activeCompany?.name || 'No Company Selected'}
            </div>
            {(ivpIndustryLabel || activeCompany?.industry) && (
              <div className="company-industry">
                {ivpIndustryLabel || activeCompany?.industry}
              </div>
            )}
          </div>
          <div className="dropdown-arrow">{showDropdown ? '▲' : '▼'}</div>
        </button>
      </div>

      {showDropdown && (
        <>
          <div className="dropdown-overlay" onClick={() => setShowDropdown(false)} />
          <div className="company-dropdown">
            <div className="dropdown-header">
              <span>Select Company</span>
              <button className="close-dropdown" onClick={() => setShowDropdown(false)}>×</button>
            </div>

            <div className="company-list">
              {companies.length === 0 ? (
                <div className="empty-companies">No companies yet</div>
              ) : (
                companies.map((company) => (
                  <div
                    key={company.id}
                    className={`company-item ${company.id === activeCompanyId ? 'active' : ''}`}
                    onClick={() => handleSelectCompany(company.id)}
                  >
                    <div className="company-item-info">
                      <div className="company-item-name">
                        {company.name}
                        {company.isSample && <span className="sample-badge">SAMPLE</span>}
                        {healthScores[company.id] !== undefined && (
                          <span className={`health-dot ${healthScores[company.id] >= 70 ? 'good' : healthScores[company.id] >= 40 ? 'warn' : 'critical'}`} />
                        )}
                      </div>
                      {(company.industry || (company.isSample && company.sampleSlug)) && (
                        <div className="company-item-industry">
                          {company.industry}
                          {company.isSample && company.sampleSlug && (
                            <span
                              className="sample-slug-pill"
                              title={`Sample vertical: ${company.sampleSlug}`}
                            >
                              {company.sampleSlug}
                            </span>
                          )}
                        </div>
                      )}
                    </div>
                    <button
                      className="delete-company"
                      onClick={(e) => handleDeleteCompany(company.id, e)}
                      title="Delete company"
                    >
                      ×
                    </button>
                  </div>
                ))
              )}
            </div>

            <div className="dropdown-actions">
              <button
                className={`action-btn ${NEW_COMPANY_CTA_ENABLED ? '' : 'action-btn-disabled'}`}
                aria-disabled={!NEW_COMPANY_CTA_ENABLED}
                onClick={openOnboardingWizard}
              >
                + New Company
              </button>
              {activeCompany && (
                <button className="action-btn" onClick={openEditModal}>
                  ✎ Edit Company
                </button>
              )}
            </div>
          </div>
        </>
      )}

      {/* Edit Company Modal */}
      {showEditModal && (
        <>
          <div className="modal-overlay" onClick={() => setShowEditModal(false)} />
          <div className="modal">
            <div className="modal-header">
              <h3>Edit Company</h3>
              <button className="close-modal" onClick={() => setShowEditModal(false)}>×</button>
            </div>
            <form onSubmit={handleEditCompany}>
              <div className="form-group">
                <label>Company Name *</label>
                <input
                  type="text"
                  value={formData.name}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                  required
                  autoFocus
                />
              </div>
              <div className="form-group">
                <label>Industry</label>
                <input
                  type="text"
                  value={formData.industry}
                  onChange={(e) => setFormData({ ...formData, industry: e.target.value })}
                />
              </div>
              <div className="form-group">
                <label>Description</label>
                <textarea
                  value={formData.description}
                  onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                  rows={3}
                />
              </div>
              <div className="modal-actions">
                <button type="button" className="btn-secondary" onClick={() => setShowEditModal(false)}>
                  Cancel
                </button>
                <button type="submit" className="btn-primary">
                  Save Changes
                </button>
              </div>
            </form>
          </div>
        </>
      )}

      {/* Delete Company Confirmation Modal */}
      {companyToDelete && (
        <>
          <div className="modal-overlay" onClick={cancelDelete} />
          <div className="modal delete-confirm-modal">
            <div className="modal-header">
              <h3>Delete company?</h3>
              <button className="close-modal" onClick={cancelDelete} disabled={deleting}>×</button>
            </div>
            <div className="delete-confirm-body">
              <p>
                Permanently delete <strong>{companyToDelete.name}</strong>?
              </p>
              <p className="delete-warning">
                This removes all conversations, reports, scenarios, Vigil scans,
                and uploaded data for this company. This cannot be undone.
              </p>
              {companyToDelete.isSample && (
                <p className="delete-warning">
                  <strong>Note:</strong> This is the sample company and is read-only —
                  the delete will be rejected by the server.
                </p>
              )}
              {deleteError && (
                <p className="delete-error">{deleteError}</p>
              )}
            </div>
            <div className="modal-actions">
              <button type="button" className="btn-secondary" onClick={cancelDelete} disabled={deleting}>
                Cancel
              </button>
              <button
                type="button"
                className="btn-danger"
                onClick={confirmDelete}
                disabled={deleting}
              >
                {deleting ? 'Deleting…' : 'Delete permanently'}
              </button>
            </div>
          </div>
        </>
      )}
    </div>
  )
}

export default CompanySelector
